import {AuthModel, BusinessModel} from './_models'
import {toast} from 'react-toastify'
import {AxiosError, AxiosResponse} from 'axios'
import {refreshToken} from './_requests'
import {LabelModel} from '../../settings/core/_models'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}
const APP_LOCAL_STORAGE_KEY = 'kt-app-react-v'
const getApp = (): string | null => {
  return localStorage.getItem(APP_LOCAL_STORAGE_KEY)
}

const setApp = (app: string) => {
  if (!localStorage) {
    return
  }
  localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, app)
}

const BUSINESS_LOCAL_STORAGE_KEY = 'kt-business-react-v'
const getBusiness = (): BusinessModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(BUSINESS_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const business: BusinessModel = JSON.parse(lsValue) as BusinessModel
    if (business) {
      // You can easily check business_token expiration also
      return business
    }
  } catch (error) {
    console.error('BUSINESS LOCAL STORAGE PARSE ERROR', error)
  }
}

const setBusiness = (business: BusinessModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(business)
    localStorage.setItem(BUSINESS_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('BUSINESS LOCAL STORAGE SAVE ERROR', error)
  }
}
const removeBusiness = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(BUSINESS_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('BUSINESS LOCAL STORAGE REMOVE ERROR', error)
  }
}
const LABEL_LOCAL_STORAGE_KEY = 'kt-label-react-v'
const getLabel = (): LabelModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(LABEL_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const label: LabelModel = JSON.parse(lsValue) as LabelModel
    if (label) {
      // You can easily check label_token expiration also
      return label
    }
  } catch (error) {
    console.error('LABEL LOCAL STORAGE PARSE ERROR', error)
  }
}

const setLabel = (label: LabelModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(label)
    localStorage.setItem(LABEL_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('LABEL LOCAL STORAGE SAVE ERROR', error)
  }
}
const removeLabel = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(LABEL_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('LABEL LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config: {url: string; headers: {Authorization?: string; BusinessId?: number}}) => {
      const auth = getAuth()
      const business = getBusiness()
      if (auth && auth.auth_token) config.headers.Authorization = `Bearer ${auth.auth_token}`
      else if (business && business.BUSINESS_ID) config.headers.BusinessId = business.BUSINESS_ID
      if (process.env.REACT_APP_API_URL && config.url.startsWith(process.env.REACT_APP_API_URL)) {
        let config_url
        const api_url = config.url.split(process.env.REACT_APP_API_URL)[1]
        const app = getApp()
        if (app) config_url = process.env.REACT_APP_API_URL + '/' + app + api_url
        else {
          const app_url = window.location.href.split(window.location.origin)[1]
          process.env.REACT_APP_LIST?.split(',').forEach((a) => {
            if (app_url.startsWith(a)) config_url = process.env.REACT_APP_API_URL + a + api_url
          })
        }
        if (!config_url) {
          if (window.location.origin === process.env.REACT_APP_HAIRSTYLE_SITE_URL)
            config_url = process.env.REACT_APP_API_URL + '/hairstyle' + api_url
          else if (window.location.origin === process.env.REACT_APP_DISHES_SITE_URL)
            config_url = process.env.REACT_APP_API_URL + '/dishes' + api_url
        }
        if (config_url) config.url = config_url
      }
      return config
    },
    (err: any) => Promise.reject(err)
  )
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      response.data?.message && toast.success(response.data.message)
      return response.data.data
    },
    async (error: AxiosError) => {
      const auth = getAuth()
      const originalRequest: any = error.config
      if (error?.response?.status === 403 && auth?.refresh_token && !originalRequest._retry) {
        try {
          originalRequest._retry = true
          const response = await refreshToken(auth.refresh_token)
          setAuth(response)
          return axios(originalRequest)
        } catch (error) {
          removeAuth()
          throw error
        }
      } else if (error?.response?.status === 401) {
        removeAuth()
        throw error
      } else {
        const data: any = error?.response?.data
        if (data?.message) toast.error(data.message)
        throw error
      }
    }
  )
}

export {
  getAuth,
  setAuth,
  removeAuth,
  getBusiness,
  setBusiness,
  removeBusiness,
  getLabel,
  setLabel,
  removeLabel,
  setApp,
  AUTH_LOCAL_STORAGE_KEY,
}
